import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SidebarMenu from './SidebarMenu';
import { usePermission } from '../../hooks/usePermission';
import {
  LayoutDashboard,
  Building2,
  Home,
  Users,
  Calendar,
  DollarSign,
  Briefcase,
  UserCog,
  X,
  ChevronDown,
  Plus,
  CheckCircle,
  HandCoins,
  FileCheck,
  ShieldCheck,
  Key,
  Activity,
  UserCheck,
  Settings,
  Share2,
  User
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { canView } = usePermission();
  const isManager = user?.is_manager || user?.role === 'office_owner';

  // Menü yapısını tarayarak, verilen pathname'e göre hangi stateKey'lerin açık olması gerektiğini bul
  const getOpenMenusForPath = useCallback((pathname, items) => {
    const result = {};

    const findActiveKeys = (menuItems) => {
      for (const item of menuItems) {
        if (!item.hasSubmenu && !item.hasNestedSubmenu) continue;

        const stateKey = item.stateKey;
        if (!stateKey) continue;

        // Bu menü altındaki tüm path'leri kontrol et
        const hasActivePath = checkSubmenuForPath(pathname, item.submenu);

        // Eğer ana path eşleşiyorsa da açık olsun
        const isMainPathActive = item.path && pathname.startsWith(item.path) && item.path !== '/';

        // alwaysOpen olan menüler her zaman açık kalır
        if (item.alwaysOpen) {
          result[stateKey] = true;
        } else {
          result[stateKey] = hasActivePath || isMainPathActive;
        }

        // Alt menüleri de tara
        if (item.submenu) {
          for (const sub of item.submenu) {
            if (sub.hasNestedSubmenu && sub.stateKey) {
              const nestedActive = checkSubmenuForPath(pathname, sub.submenu);
              result[sub.stateKey] = nestedActive;
            }
          }
        }
      }
    };

    const checkSubmenuForPath = (path, submenu) => {
      if (!submenu) return false;
      return submenu.some(sub => {
        if (sub.path && path.startsWith(sub.path)) return true;
        if (sub.hasNestedSubmenu && sub.submenu) {
          return checkSubmenuForPath(path, sub.submenu);
        }
        return false;
      });
    };

    findActiveKeys(items);
    return result;
  }, []);

  const [openMenus, setOpenMenus] = useState({});

  const toggleMenu = (key) => {
    setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const menuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    canView('projects') ? {
      path: '/projects',
      icon: Building2,
      label: 'Projeler',
      hasSubmenu: true,
      alwaysOpen: true,
      stateKey: 'projects',
      submenu: [
        { path: '/projects/new', icon: Plus, label: 'Proje Ekle' }
      ]
    } : null,
    canView('portfolios') ? {
      path: '/portfolios',
      icon: Home,
      label: 'Portföyler',
      hasSubmenu: true,
      alwaysOpen: true,
      stateKey: 'portfolios',
      submenu: [
        { path: '/portfolios/my', icon: User, label: 'Portföylerim' },
        { path: '/portfolios/sales', icon: Building2, label: 'Satılık İlanlar' },
        { path: '/portfolios/rentals', icon: Key, label: 'Kiralık İlanlar' },
        { path: '/portfolios', icon: Home, label: 'Tüm Portföyler' },
        { path: '/portfolios/share-requests', icon: Share2, label: 'Paylaşım Talepleri' },
        { path: '/portfolios/new', icon: Plus, label: 'Yeni Ekle' }
      ]
    } : null,
    canView('customers') ? {
      path: '/customers',
      icon: Users,
      label: 'Müşteriler',
      hasSubmenu: true,
      alwaysOpen: true,
      stateKey: 'customers',
      submenu: [
        { path: '/customers/potential', icon: Users, label: 'Potansiyel Müşteriler' },
        { path: '/customers/active', icon: UserCheck, label: 'Müşteriler' }
      ]
    } : null,
    canView('meetings') ? {
      path: '/meetings',
      icon: Calendar,
      label: 'Görüşmeler',
      hasSubmenu: true,
      alwaysOpen: true,
      stateKey: 'meetings',
      submenu: [
        { path: '/meetings/today', icon: Calendar, label: 'Bugünkü Görüşmeler' },
        { path: '/meetings/future', icon: Calendar, label: 'Gelecek Görüşmeler' },
        { path: '/meetings/past', icon: Calendar, label: 'Geçmiş Görüşmeler' }
      ]
    } : null,
    canView('sales') ? { path: '/sales', icon: DollarSign, label: 'Satışlar' } : null,
    canView('rentals') ? { path: '/rentals', icon: Key, label: 'Kiralamalar' } : null,
    canView('down_payments') ? { path: '/down-payments', icon: Briefcase, label: 'Kaporalar' } : null,
    canView('commissions') ? { path: '/my-commissions', icon: HandCoins, label: 'Komisyonlarım' } : null,
    (user?.role === 'office_owner' || user?.is_manager) ? { path: '/finance/office', icon: DollarSign, label: 'Ofis Finans' } : null,
  ].filter(Boolean);

  menuItems.push(
    { path: '/departments/activities', icon: Activity, label: 'Son Aktiviteler' },
    canView('settings') ? {
      path: '/settings/office',
      icon: Settings,
      label: 'Ayarlar',
      hasSubmenu: true,
      alwaysOpen: true,
      stateKey: 'settings',
      submenu: [
        canView('users') ? { path: '/departments/users', icon: UserCheck, label: 'Personeller' } : null,
        canView('departments') ? { path: '/departments', icon: UserCog, label: 'Departmanlar' } : null,
        { path: '/settings/office', icon: Building2, label: 'Ofis Ayarları' },
        { path: '/settings/subscription', icon: ShieldCheck, label: 'Abonelik' },
        { path: '/departments/permissions', icon: ShieldCheck, label: 'Yetki Yönetimi' }
      ]
    } : null,
  );

  const finalMenuItems = menuItems.filter(Boolean);


  // Route değiştiğinde menüleri güncelle
  useEffect(() => {
    const newOpenMenus = getOpenMenusForPath(location.pathname, finalMenuItems);
    setOpenMenus(newOpenMenus);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-screen fixed left-0 top-0 overflow-y-auto z-30 transition-colors">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-center">
            <img src="/logo.png" alt="Propo CRM Logo" className="w-48 rounded-lg" />
          </div>
        </div>

        <nav className="p-4">
          <SidebarMenu
            items={finalMenuItems}
            openMenus={openMenus}
            toggleMenu={toggleMenu}
          />
        </nav>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 ease-in-out"
          onClick={onClose}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={`lg:hidden fixed inset-y-0 left-0 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-50 transform transition-transform duration-300 ease-out shadow-2xl ${isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-600 dark:text-primary-400">Propo CRM</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Müşteri ve Gayrimenkul Yönetim Sistemi</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-600 dark:text-gray-300"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="p-4 overflow-y-auto h-[calc(100vh-88px)]">
          <SidebarMenu
            items={finalMenuItems}
            openMenus={openMenus}
            toggleMenu={toggleMenu}
            onClose={onClose}
          />
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
